import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {TActionButtonOption} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {AddButton, AddButtonText, AddIcon, PermissionCheck} from 'src/containers/AddButton';
import {EResellerIds, useReseller} from 'src/containers/User';
import {CardIcon, DocumentationIcon} from 'src/icons';

import {
  StripeAutopaymentConnectButton,
  StripeAutopaymentInfo,
  useStripeAutopaymentButtonOption,
  useStripeAutopaymentEnabled,
} from './Autopayment';
import {InvoiceButton, showInvoiceModal} from './Invoice';
import {
  PayKeeperOnetimePaymentButton,
  PayKeeperOnetimePaymentInfo,
  StripeOnetimePaymentButton,
  showPayKeeperOnetimePaymentSettingsModal,
  showStripeOnetimePaymentSettingsModal,
  usePayKeeperOnetimePaymentEnabled,
  useStripeOnetimePaymentEnabled,
} from './OnetimePayment';
import * as S from './style';

const BalanceReplenishment = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const isStripeAutopaymentEnabled = useStripeAutopaymentEnabled();
  const isStripeOnetimePaymentEnabled = useStripeOnetimePaymentEnabled();
  const isPayKeeperOnetimePaymentEnabled = usePayKeeperOnetimePaymentEnabled();

  return (
    <>
      {isStripeAutopaymentEnabled && <StripeAutopaymentInfo />}
      {isPayKeeperOnetimePaymentEnabled && <PayKeeperOnetimePaymentInfo />}
      <Stack size="1">
        <Text color="grey600" size="L" weight={500}>
          {t('BalanceReplenishment:topUpBalance')}
        </Text>
        <Stack direction="ROW" align="center" size="2">
          <InvoiceButton />
          {isPayKeeperOnetimePaymentEnabled && !supportTenantId && (
            <PayKeeperOnetimePaymentButton />
          )}
          {isStripeOnetimePaymentEnabled && <StripeOnetimePaymentButton />}
          {isStripeAutopaymentEnabled && <StripeAutopaymentConnectButton />}
        </Stack>
      </Stack>
    </>
  );
});

const MFMButton = React.memo(() => {
  const {t} = useTranslation();

  const stripeAutopaymentOption = useStripeAutopaymentButtonOption();
  const isStripeOnetimePaymentEnabled = useStripeOnetimePaymentEnabled();

  const options = React.useMemo(() => {
    const allowedOptions: TActionButtonOption[] = [
      {
        content: (
          <>
            <S.OptionIcon as={DocumentationIcon} />
            {t('BalanceReplenishment:actions.generateInvoice')}
          </>
        ),
        handler: showInvoiceModal,
      },
    ];

    if (stripeAutopaymentOption) {
      allowedOptions.push(stripeAutopaymentOption);
    }

    if (isStripeOnetimePaymentEnabled) {
      allowedOptions.push({
        content: (
          <>
            <S.OptionIcon as={CardIcon} />
            {t('BalanceReplenishment:actions.payByCard')}
          </>
        ),
        handler: showStripeOnetimePaymentSettingsModal,
      });
    }

    return allowedOptions;
  }, [t, stripeAutopaymentOption, isStripeOnetimePaymentEnabled]);

  return (
    <AddButton permission={PermissionCheck} options={options}>
      <AddIcon />
      <AddButtonText>{t('BalanceReplenishment:actions.topUp')}</AddButtonText>
    </AddButton>
  );
});

const BalanceReplenishmentButton = React.memo(() => {
  const {t} = useTranslation();
  const isMFM = useReseller(EResellerIds.MFM_ID);
  const isPayKeeperOnetimePaymentEnabled = usePayKeeperOnetimePaymentEnabled();
  const {id: supportTenantId} = useParams<'id'>();

  if (isMFM) {
    return <MFMButton />;
  }

  if (isPayKeeperOnetimePaymentEnabled && !supportTenantId) {
    const options: TActionButtonOption[] = [
      {
        content: (
          <>
            <S.OptionIcon as={DocumentationIcon} />
            {t('BalanceReplenishment:actions.generateInvoice')}
          </>
        ),
        handler: showInvoiceModal,
      },
      {
        content: (
          <>
            <S.OptionIcon as={CardIcon} />
            {t('BalanceReplenishment:actions.payByCard')}
          </>
        ),
        handler: showPayKeeperOnetimePaymentSettingsModal,
      },
    ];

    return (
      <AddButton permission={PermissionCheck} options={options}>
        <AddIcon />
        <AddButtonText>{t('BalanceReplenishment:actions.topUp')}</AddButtonText>
      </AddButton>
    );
  }

  return (
    <AddButton onClick={showInvoiceModal} permission={PermissionCheck}>
      <AddIcon />
      <AddButtonText>{t('BalanceReplenishment:actions.topUp')}</AddButtonText>
    </AddButton>
  );
});

export {BalanceReplenishment, BalanceReplenishmentButton};
